/**
 * effectiveTemplateConfig — single resolver for template-effective
 * section order, visibility, column layouts, and presentation fields.
 *
 * Used by SiteRagReportView, DOCX export, report generation stamping,
 * and effectiveSectionOrder so `sectionsByReportType` propagates
 * consistently outside Template Studio preview.
 */
import type { ReportTypeId } from '../config/reportTypes';
import { getSectionsForType, stripRetiredSectionIds } from '../config/reportSections';
import type { GridLedStandbyPlanningReportInput } from './generationImpact/gridLedStandbyPlanningReportContent';
import {
  getEffectiveSections,
  type ReportTemplate,
  type SectionConfig,
} from '../types/reportTemplate';
import { isCustomBriefQuestionSectionId } from './customBriefSections';
import { SUB_SECTION_IDS, rendererSectionById } from '../config/rendererSectionRegistry';
import { augmentWithSubSections } from './resolveStudioSectionList';
import {
  buildColumnOverridesFromSections,
  buildSectionPagesFromSections,
} from './resolveSectionPageColumnCount';

export interface EffectiveTemplateConfig {
  sectionIds: string[];
  visibility: Record<string, boolean>;
  columnLayouts: Record<string, 1 | 2 | 3>;
  /** TSE-2 C10 — per-section page column override (inherit / single / double). */
  columnOverrides: Record<string, SectionConfig['columnOverride']>;
  /** Section → page number (1-based) from template `sections[].page`. */
  sectionPages: Record<string, number>;
  headingOverrides: Record<string, string>;
  pageLayouts: ReportTemplate['pageLayouts'];
  coverLayout: ReportTemplate['coverLayout'];
  disclaimer: string | undefined;
  glossaryEnabled: boolean | undefined;
  dynamicOrder: boolean | undefined;
  themePartial: ReportTemplate['theme'];
}

/** Ids allowed in template order beyond the type's canonical registry. */
export function isAllowedExtraSectionId(
  reportType: ReportTypeId | undefined | null,
  sectionId: string,
): boolean {
  if (SUB_SECTION_IDS.has(sectionId)) return true;
  if (isCustomBriefQuestionSectionId(sectionId)) return reportType === 'custom-brief';
  return false;
}

/**
 * Filter template section ids to those valid for `reportType`, insert
 * omitted canonical ids at their registry slot, and preserve sub-section
 * / custom-brief Q ids that appear in the template list.
 */
export function projectTemplateSectionIds(
  reportType: ReportTypeId | undefined | null,
  templateSectionIds: readonly string[] | undefined,
  reportSectionOrder?: readonly string[],
  report?: GridLedStandbyPlanningReportInput | null,
): string[] {
  const canonical = getSectionsForType(reportType, report).map(s => s.sectionId);
  const canonicalSet = new Set(canonical);
  if (!templateSectionIds?.length) return [...canonical];

  const filtered = templateSectionIds.filter(
    id => canonicalSet.has(id) || isAllowedExtraSectionId(reportType, id),
  );
  const seen = new Set(filtered);
  for (const id of canonical) {
    if (seen.has(id)) continue;
    const canonIdx = canonical.indexOf(id);
    let insertAt = filtered.length;
    for (let j = canonIdx - 1; j >= 0; j--) {
      const anchorIdx = filtered.indexOf(canonical[j]!);
      if (anchorIdx >= 0) {
        insertAt = anchorIdx + 1;
        break;
      }
    }
    filtered.splice(insertAt, 0, id);
    seen.add(id);
  }
  // Preserve custom-brief Q ids from report order when template lists them
  if (reportType === 'custom-brief' && reportSectionOrder?.length) {
    for (const id of reportSectionOrder) {
      if (isCustomBriefQuestionSectionId(id) && !seen.has(id)) {
        filtered.push(id);
        seen.add(id);
      }
    }
  }
  return filtered;
}

function buildVisibilityFromSections(sections: SectionConfig[] | null | undefined): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!Array.isArray(sections)) return out;
  for (const s of sections) {
    out[s.sectionId] = s.visible !== false;
  }
  return out;
}

function buildColumnLayoutsFromSections(sections: SectionConfig[] | null | undefined): Record<string, 1 | 2 | 3> {
  const out: Record<string, 1 | 2 | 3> = {};
  if (!Array.isArray(sections)) return out;
  for (const s of sections) {
    if (s.columnLayout === 1 || s.columnLayout === 2 || s.columnLayout === 3) {
      out[s.sectionId] = s.columnLayout;
    }
  }
  return out;
}

/**
 * Resolve template-derived section ids (ordered, type-filtered).
 * Prefers structured `getEffectiveSections` order when non-empty
 * (Sections tab drag-reorder writes `sections` / `sectionsByReportType`,
 * not legacy `sectionOrder`). Falls back to `sectionOrder` only when
 * no structured sections exist. Sub-section ids are augmented after
 * their parent to match Template Studio list parity.
 */
/**
 * Filter template section ids to those valid for `reportType`, preserving
 * the template's drag-reorder sequence (no registry back-fill inserts).
 */
function filterTemplateSectionIdsForType(
  reportType: ReportTypeId | undefined | null,
  ids: readonly string[],
  reportSectionOrder?: readonly string[],
  report?: GridLedStandbyPlanningReportInput | null,
): string[] {
  const canonicalSet = new Set(getSectionsForType(reportType, report).map(s => s.sectionId));
  const filtered = ids.filter(
    id => canonicalSet.has(id) || isAllowedExtraSectionId(reportType, id),
  );
  if (reportType === 'custom-brief' && reportSectionOrder?.length) {
    const seen = new Set(filtered);
    for (const id of reportSectionOrder) {
      if (isCustomBriefQuestionSectionId(id) && !seen.has(id)) {
        filtered.push(id);
        seen.add(id);
      }
    }
  }
  return stripRetiredSectionIds(filtered);
}

export function resolveTemplateSectionIds(
  template: ReportTemplate,
  reportType: ReportTypeId | undefined | null,
  reportSectionOrder?: readonly string[],
  report?: GridLedStandbyPlanningReportInput | null,
): string[] {
  const effectiveSections = getEffectiveSections(template, reportType ?? 'feasibility');
  if (Array.isArray(effectiveSections) && effectiveSections.length > 0) {
    const augmented = augmentWithSubSections(effectiveSections);
    return filterTemplateSectionIdsForType(
      reportType,
      augmented.map(s => s.sectionId),
      reportSectionOrder,
      report,
    );
  }
  if (template.sectionOrder && template.sectionOrder.length > 0) {
    return projectTemplateSectionIds(reportType, template.sectionOrder, reportSectionOrder, report);
  }
  return projectTemplateSectionIds(reportType, undefined, reportSectionOrder, report);
}

/**
 * Pure resolver — never mutates the template.
 */
export function resolveEffectiveTemplateConfig(
  template: ReportTemplate,
  reportType: ReportTypeId | undefined | null,
  reportSectionOrder?: readonly string[],
): EffectiveTemplateConfig {
  const effectiveSections = getEffectiveSections(template, reportType ?? 'feasibility');
  const visibility = buildVisibilityFromSections(effectiveSections);
  if (template.sectionVisibility) {
    for (const [k, v] of Object.entries(template.sectionVisibility)) {
      visibility[k] = !!v;
    }
  }

  const columnLayouts = buildColumnLayoutsFromSections(effectiveSections);
  if (template.sectionColumnLayouts) {
    for (const [k, v] of Object.entries(template.sectionColumnLayouts)) {
      if (v === 1 || v === 2 || v === 3) columnLayouts[k] = v;
    }
  }

  const columnOverrides = buildColumnOverridesFromSections(effectiveSections);
  const sectionPages = buildSectionPagesFromSections(effectiveSections);

  return {
    sectionIds: resolveTemplateSectionIds(template, reportType, reportSectionOrder),
    visibility,
    columnLayouts,
    columnOverrides,
    sectionPages,
    headingOverrides: { ...(template.headingOverrides ?? {}) },
    pageLayouts: template.pageLayouts,
    coverLayout: template.coverLayout,
    disclaimer: template.disclaimer,
    glossaryEnabled: template.glossaryEnabled,
    dynamicOrder: template.dynamicOrder,
    themePartial: template.theme,
  };
}

/**
 * Resolve section visibility for a report + optional template.
 * Report snapshot wins when explicitly boolean; else template; else true.
 */
export function resolveSectionVisibility(
  sectionId: string,
  reportVisibility: Record<string, boolean> | undefined,
  templateConfig: EffectiveTemplateConfig | null | undefined,
): boolean {
  const reportLevel = reportVisibility?.[sectionId];
  if (typeof reportLevel === 'boolean') return reportLevel;
  const tmplLevel = templateConfig?.visibility[sectionId];
  if (typeof tmplLevel === 'boolean') return tmplLevel;
  if (SUB_SECTION_IDS.has(sectionId)) {
    const parentId = rendererSectionById(sectionId)?.parentSectionId;
    if (parentId) {
      return resolveSectionVisibility(parentId, reportVisibility, templateConfig);
    }
  }
  return true;
}

/**
 * Resolve column layout: report snapshot → template → theme `bodyColumns`
 * fallback → default 1.
 *
 * PRECEDENCE (highest → lowest):
 *   1. report-level explicit `sectionColumnLayouts[sectionId]`;
 *   2. template-level explicit `columnLayouts[sectionId]`;
 *   3. the theme's `bodyColumns` DEFAULT (two-column academic presets set
 *      this to 2) — applied ONLY when the section has no explicit layout;
 *   4. `1` (today's legacy default).
 *
 * `themeBodyColumns` is OPTIONAL and defaults to `undefined` ⇒ the prior
 * behaviour (single column), so existing callers are byte-stable. A user's
 * per-section override (1/2) ALWAYS wins over the theme default.
 */
export function resolveSectionColumnLayout(
  sectionId: string,
  reportColumnLayouts: Record<string, 1 | 2 | 3> | undefined,
  templateConfig: EffectiveTemplateConfig | null | undefined,
  themeBodyColumns?: 1 | 2,
): 1 | 2 | 3 {
  // #163 — a single-column PRESET is AUTHORITATIVE: a theme with
  // `bodyColumns: 1` (Journal / Institutional / Monograph) forces every section
  // to ONE column, beating any report/template per-section column layout. Before
  // this, a template that stamped a 2-col section overrode the preset, so a
  // 1-column theme rendered 2-column content and switching the preset had NO
  // visible effect on the layout. `themeBodyColumns` is OPTIONAL — when absent
  // (legacy / no-preset callers) this is skipped and the prior precedence below
  // applies unchanged, so existing reports stay byte-stable.
  if (themeBodyColumns === 1) return 1;
  const reportLevel = reportColumnLayouts?.[sectionId];
  if (reportLevel === 1 || reportLevel === 2 || reportLevel === 3) return reportLevel;
  const tmplLevel = templateConfig?.columnLayouts[sectionId];
  if (tmplLevel === 1 || tmplLevel === 2 || tmplLevel === 3) return tmplLevel;
  if (themeBodyColumns === 2) return 2;
  return 1;
}
